/**
 * App-level Sentry init — the GLOBAL-hub counterpart to the isolated library
 * client in `./index.ts`.
 *
 * ── WHY A SECOND ENTRYPOINT, NOT A FLAG ON THE FIRST ────────────────────────
 *
 * `./index.ts` deliberately never touches the global hub, because a LIBRARY has
 * no business reporting errors it did not cause. An APP is the opposite case: it
 * owns the page, so `Sentry.init()` — and the default global `error` /
 * `unhandledrejection` handlers that come with it — is exactly right for it.
 *
 * Those are different jobs with opposite correct answers, so they are separate
 * modules rather than one function with a boolean. Importing `@gjl/sentry` still
 * gets the isolated client and still pays nothing at runtime; importing
 * `@gjl/sentry/app` is an explicit opt-in to shipping the SDK.
 *
 * ── WHY IT EXISTS ───────────────────────────────────────────────────────────
 *
 * Measured 2026-08-12: exactly ONE frontend out of ~52 Vite builds in this repo
 * called `Sentry.init()` — `fugue-lab/app`. The marketing site, both paying
 * client sites and every demo site were dark. That is the inversion
 * `docs/pm/active/frontend-error-reporting.md` §1 names: a React crash on a
 * static site is an HTTP 200, the `rate(5 minutes)` site monitor reports UP,
 * there is no server to log anything, and nobody finds out until someone loads
 * the page.
 *
 * The alternative was copying `app/src/lib/sentry.ts` — ~190 lines carrying the
 * PII scrubbing and the replay-quota reasoning — into each site. That is how
 * this repo acquired three SES senders and two contact-form implementations, and
 * the second contact form is the reason THIS package exists at all.
 *
 * ── ERRORS ONLY, AND THE INTEGRATIONS ARE NOT EVEN IMPORTED ─────────────────
 *
 * No tracing, no session replay. Not merely sampled to zero — `replayIntegration`
 * and `browserTracingIntegration` are never referenced, so a bundler drops them.
 * Two reasons, the bundle one secondary:
 *
 *  1. Sentry quotas are per-ORGANISATION, not per-project. These sites draw on
 *     the same pool as the Fugue Lab app, and replay volume is what actually
 *     exhausts a quota — the app's own config already sets
 *     `replaysSessionSampleRate: 0` for precisely this reason. Turning replay on
 *     across a marketing site plus two client sites would spend the quota on
 *     healthy sessions and then drop the events that mattered.
 *  2. Replay is the largest single piece of the browser SDK, and these are
 *     static sites whose selling point is being fast.
 *
 * A surface that genuinely wants tracing or replay should make that a deliberate
 * quota decision and call `Sentry.init` itself, which is what `fugue-lab/app`
 * does. It is not a parameter here on purpose.
 */

import * as Sentry from '@sentry/react'

/** Options for {@link initAppSentry}. Every value comes from the caller's build. */
export interface AppSentryOptions {
  /**
   * The DSN, read from the consuming build's env. Absent or blank disables
   * reporting and is NOT an error — that is the normal state for `npm run dev`
   * and for a CI compile check, neither of which should report anything.
   */
  dsn?: string
  /**
   * Deploy environment (`dev`, `prod`, …). Worth passing explicitly because
   * Vite's `MODE` is always `production` for any `vite build` and so cannot
   * distinguish one deploy from another.
   */
  environment?: string
  /**
   * The deployed git sha. Sentry matches an event to uploaded source maps BY
   * RELEASE, so leaving this unset keeps stack traces minified even once map
   * upload is wired — the failure mode `app/src/lib/sentry.ts` documents having
   * hit for months.
   */
  release?: string
  /** Tags applied to every event — at minimum which site sent it. */
  tags?: Record<string, string>
}

/**
 * The parts of a Sentry event this module rewrites. Structural rather than an
 * `ErrorEvent` import so {@link scrubAppEvent} stays callable from a test
 * without constructing a full event, matching `scrubEvent` in `./index.ts`.
 */
export interface ScrubbableAppEvent {
  user?: unknown
  request?: unknown
  extra?: unknown
  breadcrumbs?: unknown
}

const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g

/** Keys whose VALUE is never safe to send, matched case-insensitively. */
const SENSITIVE_KEY_PATTERNS = [
  /password/i,
  /token/i,
  /secret/i,
  /authorization/i,
  /api_?key/i,
  /credit_?card/i,
  /ssn/i,
]

/**
 * NOISE THAT IS NOT OURS. Matched against the event message, so a substring
 * pattern is enough.
 *
 * `initAppSentry` installs Sentry's default global `error` /
 * `unhandledrejection` handlers, which is correct for an app that owns its page
 * — and means we also capture whatever a VISITOR'S environment throws on top of
 * it: a browser extension, an antivirus shim, a mail client's link-preview
 * webview. Those events have no frame in our bundle, no user impact, and no
 * action available to us. Left unfiltered they page a one-person team about a
 * stranger's Outlook install.
 *
 * ── THE BAR FOR ADDING A PATTERN ────────────────────────────────────────────
 *
 * A dropped event is invisible — no counter, no breadcrumb, nothing in the UI
 * says a filter fired. That is the same failure shape `guards.sh` exists to
 * avoid, so the bar is deliberately high and the list is deliberately short:
 *
 *  1. The string appears NOWHERE in this repo (`grep` it), so it cannot be ours.
 *  2. It is attributable to a specific external injector, named in the comment.
 *  3. It is specific enough that a real error cannot collide with it. Prefer an
 *     anchored pattern with its literal punctuation over a bare keyword.
 *
 * A pattern that merely looks unfamiliar does not qualify. If in doubt, resolve
 * the issue in Sentry instead — that is reversible and leaves a record.
 */
const IGNORED_ERROR_PATTERNS: (string | RegExp)[] = [
  /**
   * An injected host bridge — most often attributed to Microsoft Outlook's
   * link-preview / SafeLinks webview, though the event carries nothing that
   * PROVES which injector it was — calls back after its host object is gone.
   * The rejection carries no Error at all, so Sentry stringifies it as
   * "Non-Error promise rejection captured with value: …".
   *
   * What is certain, and is what justifies the filter: the string appears
   * nowhere in this repo, and no bundle of ours can produce it. Seen on
   * fuguelab.com 2026-08-27 (CMS-COMPONENTS-5) — 1 event, 0 users, culprit the
   * bare page URL, not one frame in our code.
   */
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
]

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The only request headers worth keeping, matched case-insensitively.
 *
 * This used to be `delete request.headers` outright, and that was a mistake with
 * a cost we only measured on 2026-08-27: Sentry derives `contexts.browser` and
 * `contexts.os` SERVER-SIDE from the User-Agent header, so deleting the header
 * bag left every event from every site on this module with an empty
 * `browser.name` and `os.name` — permanently, and for months. The first time
 * that mattered was triaging a suspected-external error, where "which browser"
 * was exactly the field that would have settled it in one look, and it was blank
 * because of our own scrubber.
 *
 * An allowlist rather than a denylist: a header bag is attacker- and
 * browser-controlled, so anything not named here is dropped, and a header that
 * gains a new leak (`cookie2`, a bearer variant) cannot arrive already trusted.
 * A User-Agent identifies a browser build, not a person, and is sent to every
 * origin the visitor contacts anyway.
 */
const REQUEST_HEADER_ALLOWLIST = new Set(['user-agent'])

/**
 * Reduce a request header bag to {@link REQUEST_HEADER_ALLOWLIST}. Returns
 * `undefined` when nothing survives, so the caller deletes the key rather than
 * shipping an empty object.
 */
function filterHeaders(headers: unknown): unknown {
  // Sentry's own type is `Record<string, string>`, but the browser SDK has also
  // shipped an array of [name, value] pairs. Anything else is not a header bag
  // and is dropped whole rather than guessed at.
  if (Array.isArray(headers)) {
    const kept = headers.filter(
      (entry) =>
        Array.isArray(entry) && typeof entry[0] === 'string' && REQUEST_HEADER_ALLOWLIST.has(entry[0].toLowerCase())
    )
    return kept.length > 0 ? kept : undefined
  }
  if (!isPlainObject(headers)) return undefined
  const kept: Record<string, unknown> = {}
  for (const [name, value] of Object.entries(headers)) {
    if (REQUEST_HEADER_ALLOWLIST.has(name.toLowerCase())) kept[name] = value
  }
  return Object.keys(kept).length > 0 ? kept : undefined
}

/** Drop the query string and fragment — either can carry a form value. */
function stripUrlParams(url: string): string {
  const cut = url.search(/[?#]/)
  return cut === -1 ? url : url.slice(0, cut)
}

function redactStrings(value: unknown, depth = 0): unknown {
  // Depth cap: `extra` is caller-supplied and may be cyclic or very deep, and a
  // reporter that stack-overflows inside `beforeSend` takes out the error path
  // it exists to serve.
  if (depth > 6) return '[TRUNCATED]'
  if (typeof value === 'string') return value.replace(EMAIL_PATTERN, '[EMAIL_REDACTED]')
  if (Array.isArray(value)) return value.map((entry) => redactStrings(entry, depth + 1))
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value)) {
      out[key] = SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key))
        ? '[REDACTED]'
        : redactStrings(entry, depth + 1)
    }
    return out
  }
  return value
}

/**
 * THE PII GATE for app-level reporting. Exported so it is tested against the
 * real function rather than a reimplementation — the same reasoning as
 * `scrubEvent` in `./index.ts`, and for the same reason: a DSN is substituted at
 * Vite TRANSFORM time, so no runtime stub can reach the client config.
 *
 * Deliberately LESS aggressive than the library's `scrubEvent`, which deletes
 * `request` and `breadcrumbs` outright. A library reports one handed-to-it
 * exception and needs no context; an app is diagnosing an unexpected crash, and
 * the path plus the breadcrumb trail are most of what makes an event actionable.
 * So this keeps them and removes the parts that can carry a person's data:
 *
 *  - `user` is deleted. These are public sites with no authenticated visitor. If
 *    one ever gains a login, restoring identity here should be a deliberate
 *    decision, not an inheritance.
 *  - URLs keep their path and lose their query string and fragment, on the event
 *    and on every breadcrumb. A contact form submitted as a GET, or an unsubscribe
 *    link, puts an email address in a query parameter.
 *  - Request headers are reduced to an allowlist — see
 *    {@link REQUEST_HEADER_ALLOWLIST} for why the User-Agent survives and
 *    everything else does not. Cookies and the request body are deleted.
 *  - `extra` and breadcrumb data get email redaction and sensitive-key removal.
 */
export function scrubAppEvent<T extends ScrubbableAppEvent>(event: T): T {
  delete event.user

  if (isPlainObject(event.request)) {
    const request = event.request
    if (typeof request.url === 'string') request.url = stripUrlParams(request.url)
    delete request.cookies
    delete request.data
    const headers = filterHeaders(request.headers)
    if (headers === undefined) delete request.headers
    else request.headers = headers
  }

  if (event.extra !== undefined) {
    event.extra = redactStrings(event.extra)
  }

  if (Array.isArray(event.breadcrumbs)) {
    for (const crumb of event.breadcrumbs) {
      if (!isPlainObject(crumb)) continue
      if (typeof crumb.message === 'string') {
        crumb.message = crumb.message.replace(EMAIL_PATTERN, '[EMAIL_REDACTED]')
      }
      if (isPlainObject(crumb.data)) {
        // `fetch`/`xhr`/`navigation` crumbs put the URL here under several keys.
        for (const key of ['url', 'from', 'to']) {
          const value = crumb.data[key]
          if (typeof value === 'string') crumb.data[key] = stripUrlParams(value)
        }
        crumb.data = redactStrings(crumb.data) as Record<string, unknown>
      }
    }
  }

  return event
}

let initialized = false

/**
 * Initialise error reporting for an APP. Call once, from the browser entry,
 * before rendering.
 *
 * Returns whether reporting is now active, so a caller can log the disabled case
 * if it wants to. Never throws: a failure to set up error reporting must not be
 * the thing that stops a site from loading.
 */
export function initAppSentry(options: AppSentryOptions = {}): boolean {
  const dsn = options.dsn?.trim()
  if (!dsn) return false
  if (initialized) return true

  try {
    Sentry.init({
      dsn,
      environment: options.environment || 'unknown',
      release: options.release,
      // See the header: errors only, and the tracing/replay integrations are not
      // imported anywhere in this module so they cannot end up in the bundle.
      integrations: (defaults) => defaults.filter((integration) => !/Replay|BrowserTracing/i.test(integration.name)),
      tracesSampleRate: 0,
      // See IGNORED_ERROR_PATTERNS: the global handlers above are what make this
      // necessary, since they capture the visitor's environment along with ours.
      ignoreErrors: IGNORED_ERROR_PATTERNS,
      beforeSend: (event) => scrubAppEvent(event),
      beforeBreadcrumb: (crumb) => {
        // Console breadcrumbs are the one category that routinely carries
        // whatever an app happened to log, including request payloads.
        if (crumb.category === 'console' && crumb.level !== 'error') return null
        return crumb
      },
    })

    if (options.tags) Sentry.setTags(options.tags)
    initialized = true
    return true
  } catch {
    // A broken reporter is not worth a broken page.
    return false
  }
}

/**
 * Report a caught error that should still be recorded — an ErrorBoundary, or a
 * `catch` that shows the visitor a fallback.
 *
 * Falls back to `console.error` when reporting is disabled, so the information
 * is never simply discarded during local development.
 */
export function captureAppError(error: unknown, context?: Record<string, unknown>): void {
  if (!initialized) {
    console.error('[sentry disabled]', error, context)
    return
  }
  try {
    Sentry.captureException(error, context ? { extra: context } : undefined)
  } catch {
    // Same reasoning as initAppSentry: never throw from the error path.
  }
}

/** Whether {@link initAppSentry} found a DSN and completed. */
export function isAppSentryInitialized(): boolean {
  return initialized
}

/** Test seam — lets a suite re-evaluate the init guard. */
export function __resetAppSentryForTests(): void {
  initialized = false
}
